import { Controller } from "@hotwired/stimulus"
import Plyr from "plyr"

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

export default class extends Controller {
  static targets = ["video", "trackButton", "overlay"]
  static values = { playlist: { type: Array, default: [] } }

  connect() {
    this.player = new Plyr(this.videoTarget, {
      controls: [
        "play-large", "play", "progress", "current-time", "duration",
        "mute", "volume", "captions", "download", "pip", "fullscreen"
      ],
      captions: { active: true, update: true },
      urls: { download: this.videoTarget.querySelector("source")?.src }
    })

    this.currentIndex = 0

    this.player.on("ready", () => {
      this.insertSpeedButton()
      this.insertCaptionsLocaleLabel()
      this.updateCaptionsTooltip()
      this.attachOverlayToPlayer()
    })

    this.player.on("captionsenabled", () => this.updateCaptionsTooltip())
    this.player.on("captionsdisabled", () => this.updateCaptionsTooltip())

    this.markActiveTrackButton()
  }

  attachOverlayToPlayer() {
    if (!this.hasOverlayTarget) return

    const container = this.player.elements.container
    if (!container) return

    container.appendChild(this.overlayTarget)
  }

  disconnect() {
    this.player?.destroy()
  }

  changeTrack(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.loadTrack(index)
    this.player.play()
  }

  loadTrack(index) {
    const track = this.playlistValue[index]
    if (!track) return

    this.currentIndex = index

    this.player.source = {
      type: "video",
      sources: [{ src: track.src, type: "video/mp4" }],
      tracks: this.tracksFor(track)
    }

    this.markActiveTrackButton()
  }

  tracksFor(track) {
    if (!track.subtitles?.src) return []

    return [{
      kind: "captions",
      label: track.subtitles.language,
      srclang: track.subtitles.language,
      src: track.subtitles.src,
      default: true
    }]
  }

  markActiveTrackButton() {
    if (!this.hasTrackButtonTarget) return

    this.trackButtonTargets.forEach(button => {
      const index = parseInt(button.dataset.index, 10)
      button.classList.toggle("active", index === this.currentIndex)
    })
  }

  insertSpeedButton() {
    const captionsButton = this.element.querySelector('[data-plyr="captions"]')
    if (!captionsButton) return

    const button = document.createElement("button")
    button.type = "button"
    button.className = "plyr__controls__item plyr__control plyr__speed-button"
    button.innerHTML = '<span class="plyr__speed-label">1x</span>'

    button.addEventListener("click", () => this.cycleSpeed(button))

    captionsButton.parentNode.insertBefore(button, captionsButton)
  }

  cycleSpeed(button) {
    const currentIndex = SPEEDS.indexOf(this.player.speed)
    const nextIndex = (currentIndex + 1) % SPEEDS.length
    this.player.speed = SPEEDS[nextIndex]

    button.querySelector(".plyr__speed-label").textContent = `${SPEEDS[nextIndex]}x`
  }

  insertCaptionsLocaleLabel() {
    const button = this.element.querySelector('[data-plyr="captions"]')
    if (!button) return

    const locale = this.formattedLocale()
    if (!locale) return

    const label = document.createElement("span")
    label.className = "plyr__captions-locale"
    label.textContent = locale
    button.appendChild(label)
  }

  formattedLocale() {
    const track = this.videoTarget.querySelector("track")
    const srclang = track?.getAttribute("srclang")
    if (!srclang) return null

    const parts = srclang.split("-")
    const code = parts.length > 1 ? parts[1] : parts[0]

    return `${code.toLowerCase()}-${code.toUpperCase()}`
  }

  updateCaptionsTooltip() {
    const button = this.element.querySelector('[data-plyr="captions"]')
    if (!button) return

    const pressedTooltip = button.querySelector(".label--pressed.plyr__tooltip")
    const notPressedTooltip = button.querySelector(".label--not-pressed.plyr__tooltip")

    const label = this.trackLabel()

    if (pressedTooltip) pressedTooltip.textContent = `Disable captions (${label})`
    if (notPressedTooltip) notPressedTooltip.textContent = `Enable captions (${label})`
  }

  trackLabel() {
    const track = this.videoTarget.querySelector("track")
    return track?.getAttribute("label") || track?.getAttribute("srclang")?.toUpperCase() || ""
  }
}
